import React, { useEffect, useState } from 'react';
import { ImageFormatProps } from '../types';
import { useImageProcessor } from '../hooks/useImageProcessor';

const ImageFormat: React.FC<ImageFormatProps> = ({
    image,
    format,
    quality = 80,
    onProcessingComplete,
    onError,
    className,
    style,
}) => {
    const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(null);
    const [formatInfo, setFormatInfo] = useState<{
        originalFormat: string;
        originalSize: number;
        convertedSize: number;
    } | null>(null);

    const { imageBlob, imageUrl, isLoading, convertFormat } = useImageProcessor({
        image,
        onError,
    });

    useEffect(() => {
        const processImage = async () => {
            if (!imageBlob) return;

            try {
                // Get original format from mime type
                const originalFormat = imageBlob.type.split('/')[1] || 'unknown';

                // Convert the image
                const convertedBlob = await convertFormat(format, quality);
                const convertedUrl = URL.createObjectURL(convertedBlob);

                setProcessedImageUrl(convertedUrl);
                setFormatInfo({
                    originalFormat,
                    originalSize: imageBlob.size,
                    convertedSize: convertedBlob.size,
                });

                if (onProcessingComplete) {
                    onProcessingComplete(convertedBlob);
                }
            } catch (err) {
                console.error('Error converting image format:', err);
                if (onError && err instanceof Error) {
                    onError(err);
                }
            }
        };

        processImage();

        // Cleanup function
        return () => {
            if (processedImageUrl) {
                URL.revokeObjectURL(processedImageUrl);
            }
        };
    }, [imageBlob, format, quality, onProcessingComplete, onError, convertFormat]);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (isLoading) {
        return <div className={className} style={style}>Loading image...</div>;
    }

    return (
        <div
            className={className}
            style={{
                ...style,
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                {/* Original Image */}
                <div style={{ flex: 1, minWidth: '250px' }}>
                    <h3>Original {formatInfo?.originalFormat.toUpperCase()}</h3>
                    {imageUrl && (
                        <img
                            src={imageUrl}
                            alt="Original"
                            style={{
                                maxWidth: '100%',
                                display: 'block',
                                border: '1px solid #ddd',
                            }}
                        />
                    )}
                    {formatInfo && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                            Size: {formatFileSize(formatInfo.originalSize)}
                        </div>
                    )}
                </div>

                {/* Converted Image */}
                <div style={{ flex: 1, minWidth: '250px' }}>
                    <h3>Converted to {format.toUpperCase()}</h3>
                    {processedImageUrl ? (
                        <img
                            src={processedImageUrl}
                            alt={`Converted to ${format}`}
                            style={{
                                maxWidth: '100%',
                                display: 'block',
                                border: '1px solid #ddd',
                            }}
                        />
                    ) : (
                        <div style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid #ddd',
                        }}>
                            Converting...
                        </div>
                    )}
                    {formatInfo && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                            <div>Size: {formatFileSize(formatInfo.convertedSize)}</div>
                            <div>
                                {formatInfo.convertedSize < formatInfo.originalSize
                                    ? `Reduced by ${((1 - formatInfo.convertedSize / formatInfo.originalSize) * 100).toFixed(1)}%`
                                    : `Increased by ${((formatInfo.convertedSize / formatInfo.originalSize - 1) * 100).toFixed(1)}%`}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div style={{ marginTop: '0.5rem' }}>
                <p style={{ fontSize: '0.9rem', color: '#666' }}>
                    Format conversion may affect image quality and file size.
                    {format === 'webp' && ' WebP generally provides better compression than JPEG and PNG.'}
                    {format === 'png' && ' PNG is lossless but typically results in larger file sizes.'}
                    {format === 'jpeg' && ' JPEG uses lossy compression and is best for photographs.'}
                    {format === 'gif' && ' GIF is limited to 256 colors and is best for simple animations.'}
                </p>
            </div>
        </div>
    );
};

export default ImageFormat; 